import React, { useState, useEffect, useMemo } from "react";
import { classNames } from "primereact/utils";
import { AppDialog } from "../../../common/components/AppDialog/AppDialog";
import { DefaultButton } from "../../../common/components/button/defaultButton/DefaultButton";
import styles from "./CalendarModal.module.scss";

export interface CalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (startDate: Date, endDate: Date) => void;
  initialStartDate?: Date;
  initialEndDate?: Date;
}

const MONTHS = [
  "Януари",
  "Февруари",
  "Март",
  "Април",
  "Май",
  "Юни",
  "Юли",
  "Август",
  "Септември",
  "Октомври",
  "Ноември",
  "Декември",
];

const DAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"];

export const CalendarModal = React.memo<CalendarModalProps>(
  ({ isOpen, onClose, onApply, initialStartDate, initialEndDate }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(
      initialStartDate || null
    );
    const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(
      initialEndDate || null
    );
    const [isSelectingRange, setIsSelectingRange] = useState(false);

    useEffect(() => {
      if (isOpen) {
        setSelectedStartDate(initialStartDate || null);
        setSelectedEndDate(initialEndDate || null);
        setIsSelectingRange(false);
        if (initialStartDate) {
          setCurrentMonth(
            new Date(
              initialStartDate.getFullYear(),
              initialStartDate.getMonth(),
              1
            )
          );
        }
      }
    }, [isOpen, initialStartDate, initialEndDate]);

    const calendar = useMemo(() => {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startDate = new Date(firstDay);

      // Adjust to start from Monday (1) instead of Sunday (0)
      const dayOfWeek = (firstDay.getDay() + 6) % 7;
      startDate.setDate(startDate.getDate() - dayOfWeek);

      const days = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      for (let i = 0; i < 42; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);

        const isCurrentMonth = date.getMonth() === month;
        const isToday = date.getTime() === today.getTime();
        const isSelected =
          (selectedStartDate &&
            date.getTime() === selectedStartDate.getTime()) ||
          (selectedEndDate && date.getTime() === selectedEndDate.getTime());
        const isInRange =
          selectedStartDate &&
          selectedEndDate &&
          date >= selectedStartDate &&
          date <= selectedEndDate;

        days.push({
          date: new Date(date),
          day: date.getDate(),
          isCurrentMonth,
          isToday,
          isSelected,
          isInRange,
        });
      }

      return days;
    }, [currentMonth, selectedStartDate, selectedEndDate]);

    const handlePrevMonth = () => {
      setCurrentMonth(
        (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
      );
    };

    const handleNextMonth = () => {
      setCurrentMonth(
        (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
      );
    };

    const handleDateClick = (date: Date) => {
      if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
        // Start new selection
        setSelectedStartDate(date);
        setSelectedEndDate(null);
        setIsSelectingRange(true);
      } else if (isSelectingRange) {
        // Complete range selection
        if (date < selectedStartDate) {
          // Auto-swap if user selects earlier date second
          setSelectedEndDate(selectedStartDate);
          setSelectedStartDate(date);
        } else {
          setSelectedEndDate(date);
        }
        setIsSelectingRange(false);
      }
    };

    const handleApply = () => {
      if (selectedStartDate && selectedEndDate) {
        onApply(selectedStartDate, selectedEndDate);
      } else if (selectedStartDate) {
        onApply(selectedStartDate, selectedStartDate);
      }
    };

    const handleCancel = () => {
      onClose();
    };

    const canApply = selectedStartDate !== null;

    return (
      <AppDialog
        visible={isOpen}
        onHide={onClose}
        showHeader={false}
        className={styles.calendarModal}
        contentClassName={styles.content}
        maskClassName={styles.calendarModalMask}
      >
        <div className={styles.calendarHeader}>
          <button
            type="button"
            className={styles.navButton}
            onClick={handlePrevMonth}
            aria-label="Предишен месец"
          >
            ←
          </button>

          <h3 className={styles.monthTitle}>
            {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>

          <button
            type="button"
            className={styles.navButton}
            onClick={handleNextMonth}
            aria-label="Следващ месец"
          >
            →
          </button>
        </div>

        <div className={styles.daysHeader}>
          {DAYS.map((day) => (
            <div key={day} className={styles.dayHeader}>
              {day}
            </div>
          ))}
        </div>

        <div className={styles.calendar}>
          {calendar.map((dayData, index) => (
            <button
              key={index}
              type="button"
              className={classNames(styles.day, {
                [styles.otherMonth]: !dayData.isCurrentMonth,
                [styles.today]: dayData.isToday,
                [styles.selected]: dayData.isSelected,
                [styles.inRange]: dayData.isInRange && !dayData.isSelected,
              })}
              onClick={() => handleDateClick(dayData.date)}
              disabled={!dayData.isCurrentMonth}
            >
              {dayData.day}
            </button>
          ))}
        </div>

        <div className={styles.actions}>
          <DefaultButton
            design="neutral"
            label="Отказ"
            onClick={handleCancel}
            className={styles.cancelButton}
          />

          <DefaultButton
            design="accent"
            label="Приложи"
            onClick={handleApply}
            disabled={!canApply}
            className={styles.applyButton}
          />
        </div>
      </AppDialog>
    );
  }
);
